import React from "react";
import { createRoot } from "react-dom/client";
import { PostHogProvider } from "@posthog/react";
import { rememberSwitchedFrom } from "@merge-api/merge-javascript-shared";

import App from "./App";
import CookieSettings from "./CookieSettings";
import { exposeDevDebugHook, posthogHost, posthogOptions, posthogToken } from "./posthog/config";
import { identifyPostHogUser } from "./posthog/identify";
import * as serviceWorker from "./serviceWorker";
import * as Sentry from "./SentrySettings";

CookieSettings.initialize();
Sentry.initialize();
rememberSwitchedFrom("unified");

// Prune residual `api_tester_log_*` entries written by PR #3915 (reverted in #3986).
// Safe to remove this block once we're confident affected users have logged in at least once.
try {
  const len = localStorage.length;
  for (let i = len - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (key?.startsWith("api_tester_log_")) localStorage.removeItem(key);
  }
} catch {
  // localStorage unavailable (private mode, blocked, sandboxed iframe) — skip
}

const container = document.getElementById("root") as HTMLElement;
const root = createRoot(container);

if (posthogToken && posthogHost) {
  root.render(
    <React.StrictMode>
      <PostHogProvider apiKey={posthogToken} options={posthogOptions}>
        <App />
      </PostHogProvider>
    </React.StrictMode>,
  );
} else {
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.warn(
      "[PostHog] Not initializing — REACT_APP_POSTHOG_PROJECT_TOKEN or REACT_APP_POSTHOG_HOST missing from .env. See .env.sample.",
    );
  }
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
}

exposeDevDebugHook(identifyPostHogUser);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
